/**
 * @fileoverview Organizer Settings Page
 * @summary Settings page for organizers with subscription plan management.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/OrganizerSettingsPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useAppDispatch } from '../../hooks/redux.js'
import UserSettingsPage from '../UserSettingsPage.js'
import { ProfileFormOrganizer } from './components/ProfileFormOrganizer.js'
import { PlanSection } from './components/PlanSection.js'
import { updatePreferences } from '../../store/slices/authSlice.js'
import { ConfirmModal } from '../../components/ConfirmModal.js'

/**
 * @summary Organizer settings page component.
 * @returns {React.ReactElement} Organizer settings page.
 */
export default function OrganizerSettingsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.auth.user)
  const dispatch = useAppDispatch()

  // Profile state
  const [firstName, setFirstName] = useState(user?.profile?.firstName || '')
  const [lastName, setLastName] = useState(user?.profile?.lastName || '')
  const [organization, setOrganization] = useState(user?.profile?.organization || '')
  const [email, setEmail] = useState(user?.email || '')
  const [isSaving, setIsSaving] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true)

      // Build preferences object with profile and email if changed
      const preferences: any = {
        profile: {
          firstName,
          lastName,
          organization,
        },
      }

      // Add email if it has changed
      if (email !== user?.email) {
        preferences.email = email
      }

      await dispatch(updatePreferences(preferences)).unwrap()
      setShowConfirmModal(false)
    } finally {
      setIsSaving(false)
      navigate('/')
    }
  }

  const handleSaveClick = () => {
    // Check if email has changed
    const emailChanged = email !== user?.email
    
    if (emailChanged) {
      // Show confirmation modal with email change info
      setShowConfirmModal(true)
    } else {
      // No email change, save directly
      handleSaveProfile()
    }
  }

  const handleConfirmSave = () => {
    handleSaveProfile()
  }

  const profileForm = (
    <ProfileFormOrganizer
      firstName={firstName}
      lastName={lastName}
      organization={organization}
      email={email}
      pendingEmail={user?.pendingEmail}
      isSaving={isSaving}
      onFirstNameChange={setFirstName}
      onLastNameChange={setLastName}
      onOrganizationChange={setOrganization}
      onEmailChange={setEmail}
      onSave={handleSaveClick}
    />
  )

  const planSection = (
    <PlanSection user={user} onNavigateToSubscription={() => navigate('/subscription')} />
  )

  // Check if email has changed for modal message
  const emailChanged = email !== user?.email

  return (
    <>
      <UserSettingsPage
        i18nPrefix="organizer"
        preProfileComponent={planSection}
        profileFormComponent={profileForm}
      />

      {/* Email Change Confirmation Modal */}
      {emailChanged && (
        <ConfirmModal
          isOpen={showConfirmModal}
          title={t('organizer.emailChangeConfirmTitle') || 'Modification de l\'adresse email'}
          message={t('organizer.emailChangeConfirmMessage') || 'Un email de confirmation sera envoyé à votre nouvelle adresse email. Votre nouveau email ne sera effectif qu\'après validation du lien dans cet email. Votre ancien email reste actif jusqu\'à la validation.'}
          confirmLabel={t('common.confirm') || 'Confirmer'}
          cancelLabel={t('common.cancel') || 'Annuler'}
          variant="info"
          onConfirm={handleConfirmSave}
          onCancel={() => setShowConfirmModal(false)}
          isLoading={isSaving}
        />
      )}
    </>
  )
}

